import { faArrowLeft } from "@fortawesome/pro-light-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons/faArrowRight";
import { faLinkSimple } from "@fortawesome/pro-regular-svg-icons/faLinkSimple";
import { faArrowRight as faArrowRightSolid } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { faRotateRight } from "@fortawesome/pro-solid-svg-icons/faRotateRight";
import { faThumbsDown } from "@fortawesome/pro-solid-svg-icons/faThumbsDown";
import { faThumbsUp } from "@fortawesome/pro-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CalculatorQuestionCategory } from "@prisma/client";
import clsx from "clsx";
import { useFormatter } from "next-intl";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "react-toastify";
import { useTranslations } from "use-intl";
import { useBoolean, useFetch } from "usehooks-ts";
import { CalculatorAnswerTypes } from "../../../../types/Co2CalculatorAnswerTypes";
import useFetchAdvanced from "../../../hooks/useFetchAdvanced";
import useSessionUser from "../../../hooks/useSessionUser";
import useUtilTranslations from "../../../hooks/useUtilTranslations";
import { useAppSelector } from "../../../redux/hooks";
import { FormatOptions } from "../../../util";
import { getIconForCategory } from "../../../util/calculator";
import {
  TSelectCalculatorSummaryTip,
  TSelectCountryAnswer,
  TSelectCurrentQuestion,
} from "../../../util/prismaHelper";
import Button from "../../Button/Button";
import Card from "../../Card/Card";
import styles from "./CalculatorSummary.module.scss";
import CategorySummaryModal from "./CategorySummaryModal";

export type SummaryProps = {};

function CalculatorSummary({}: SummaryProps) {
  const t = useTranslations("Co2CalculatorIndexPage");
  const format = useFormatter();
  const router = useRouter();
  const tUtil = useUtilTranslations();
  const state = useAppSelector((state) => state.calculator);
  const { value: isLoadingSettingCo2e, setValue: setIsLoadingSettingCo2e } =
    useBoolean(false);
  const { sessionStatus } = useSessionUser();
  const {
    data: tip,
    isLoading: isTipLoading,
    refetch,
  } = useFetchAdvanced<TSelectCalculatorSummaryTip & { text: string }>(
    "/api/co2-calculator/tip",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        locale: router.locale,
      }),
    },
    true
  );
  const [selectedCategory, setSelectedCategory] =
    useState<CalculatorQuestionCategory | null>(null);
  const { data: questions, error } = useFetch<TSelectCurrentQuestion[]>(
    selectedCategory != null
      ? "/api/co2-calculator/q/category/" + selectedCategory
      : undefined,
    {
      method: "GET",
    }
  );
  const { data: countryAnswerData } = useFetch<TSelectCountryAnswer>(
    "/api/co2-calculator/a/country@1",
    {
      method: "GET",
    }
  );

  if (!state.session) return null;

  const countryAnswer =
    countryAnswerData && countryAnswerData.QAnswer.data
      ? (
          countryAnswerData.QAnswer
            .data as CalculatorAnswerTypes.BaseRadioData[]
        ).find((d) => {
          return (
            state.answers["country@1"] &&
            state.answers["country@1"].value &&
            d.value ===
              (state.answers["country@1"].value as { [aId: string]: number })[
                countryAnswerData.QAnswer.id
              ]
          );
        })
      : null;

  const nextTip = () => {
    refetch({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        locale: router.locale,
        id: tip?.id,
      }),
    });
  };

  const goToLogin = async () => {
    return router.push("/auth/login?callback=/co2-calculator?u");
  };

  const setMonthlyCO2e = async () => {
    setIsLoadingSettingCo2e(true);
    if (sessionStatus !== "authenticated") {
      await goToLogin();
      return;
    }
    const monthlyEmission =
      Math.floor(((state.session?.yearlyCo2e ?? 0) / 12) * 100) / 100;
    if (monthlyEmission === 0) return;

    const resp = await fetch("/api/user", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        monthlyEmission: monthlyEmission,
      }),
    });
    if (resp.ok) {
      toast.success(
        t("successSettingMonthly", { value: monthlyUserCo2eFormatted })
      );
    } else {
      toast.error(t("errorSettingMonthly"));
    }
    setIsLoadingSettingCo2e(false);
  };

  const startNewCalculation = async () => {
    location.search = "r";
    // await router.replace({
    //   href: "/co2-calculator",
    //   query: "r",
    // });
    // const resp = await fetch("/api/co2-calculator/r", {
    //   method: "POST",
    // });
    // if (resp.ok) {
    //   console.log("Successfully started new calculation");
    //   location.search = "";
    //   // await router.replace({
    //   //   href: "/co2-calculator",
    //   // });
    //   // router.reload();
    // } else {
    //   toast.error(t("errorDeletingSession"));
    // }
  };

  const monthlyUserCo2eFormatted = format.number(
    (state.session.yearlyCo2e ?? 0) / 12,
    FormatOptions.kg
  );

  const goToShopOffsettingItem = () => {
    window.open("https://shop.hellogreenfriends.com/CO2-Ausgleich/", "_blank");
  };

  const isUnderYearlyCountryAverage =
    state.session.yearlyCo2e < ((countryAnswer?.compareValue as number) ?? 0);
  return (
    <div className={styles.c}>
      <Card fullWidth className={clsx(styles.baseCard, styles.overviewCard)}>
        <div className={styles.headArea}>
          <div className={styles.header}>{tUtil("summary")}</div>
          <div className={styles.yearlyOverview}>
            <div className={styles.footprint}>
              <div className={styles.yearlyLabel}>
                {t("yourYearlyCo2eUsage")}
              </div>
              <div>
                <span className={styles.co2e}>
                  {format.number((state.session.yearlyCo2e ?? 0) / 1000, {
                    maximumFractionDigits: 2,
                  })}
                </span>
                <span className={styles.co2eLabel}>{t("tonnesCo2e")}</span>
              </div>
              <div className={styles.co2eMonthly}>
                {monthlyUserCo2eFormatted}
                &nbsp;
                {tUtil("perMonth")}
              </div>
            </div>
            <div className={styles.compareContainer}>
              <FontAwesomeIcon
                className={styles.icon}
                size="2x"
                icon={isUnderYearlyCountryAverage ? faThumbsUp : faThumbsDown}
              />
              <div className={styles.compareHint}>
                {t.rich(
                  isUnderYearlyCountryAverage
                    ? "underYearlyAverage"
                    : "overYearlyAverage"
                )}
              </div>
            </div>
            <div className={styles.country}>
              {countryAnswer && (
                <>
                  <span className={styles.yearlyLabel}>
                    {tUtil("yearAverage")} in&nbsp;
                    {tUtil(
                      `countries.${
                        countryAnswer.key as keyof IntlMessages["util"]["countries"]
                      }`
                    )}
                  </span>
                  <div>
                    <span className={styles.co2e}>
                      {format.number(
                        (countryAnswer.compareValue as number) / 1000,
                        {
                          maximumFractionDigits: 2,
                        }
                      )}
                    </span>
                    <span className={styles.co2eLabel}>{t("tonnesCo2e")}</span>
                  </div>
                  <div className={styles.co2eMonthly}>
                    {format.number(
                      (countryAnswer.compareValue ?? 0) / 12,
                      FormatOptions.kg
                    )}
                    &nbsp;
                    {tUtil("perMonth")}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className={styles.summary}>
          {(
            Object.entries(state.summary) as [
              CalculatorQuestionCategory,
              Record<string, any>
            ][]
          )
            .sort(([, v1], [, v2]) =>
              v1.co2e > v2.co2e ? -1 : v1.co2e === v2.co2e ? 0 : 1
            )
            .map(([key, value]) => {
              const percent =
                (value.co2e /
                  (state.session!.yearlyCo2e === 0
                    ? 1
                    : state.session!.yearlyCo2e)) *
                100;
              const categoryCO2e = (
                <>
                  {format.number((value.co2e ?? 0) / 1000, {
                    maximumFractionDigits: 2,
                  })}
                  &nbsp;
                  {t("tonnesCo2e")}
                  &nbsp; (
                  {format.number(percent, {
                    maximumFractionDigits: 0,
                    style: "unit",
                    unit: "percent",
                  })}
                  )
                </>
              );
              return (
                <div
                  key={key}
                  onClick={() => {
                    setSelectedCategory(key);
                  }}
                  className={clsx(
                    styles.item,
                    styles[key.toLowerCase()],
                    percent >= 75
                      ? styles.s4
                      : percent >= 50
                      ? styles.s3
                      : percent >= 20
                      ? styles.s2
                      : null
                  )}
                >
                  <div className={styles.icon}>
                    <FontAwesomeIcon icon={getIconForCategory(key)} />
                  </div>
                  <div>
                    <div className={styles.label}>
                      {t(
                        `category.${
                          key.toLowerCase() as keyof IntlMessages["Co2CalculatorIndexPage"]["category"]
                        }`
                      )}
                    </div>
                    <div className={styles.hint}>{categoryCO2e}</div>
                  </div>
                </div>
              );
            })}
        </div>
        <CategorySummaryModal
          error={error?.message}
          key={selectedCategory}
          questions={questions}
          category={selectedCategory}
          isOpen={selectedCategory != null}
          onRequestClose={() => setSelectedCategory(null)}
        />
      </Card>
      <Card className={styles.baseCard} fullWidth>
        <div className={styles.userActionContainer}>
          <div className={styles.item}>
            <span>{t.rich("saveAsMonthlyHint")}</span>
            <Button
              color="green"
              disabled={isLoadingSettingCo2e}
              onClick={setMonthlyCO2e}
            >
              {t("saveAsMonthlyAction", { value: monthlyUserCo2eFormatted })}
              <FontAwesomeIcon icon={faLinkSimple} />
            </Button>
          </div>
          <div className={styles.item}>
            <span>{t.rich("startNowHint")}</span>
            <Button onClick={goToShopOffsettingItem} color="green">
              {t("startNowAction")}
              <FontAwesomeIcon icon={faArrowRightSolid} />
            </Button>
          </div>
          <div className={styles.item}>
            <span>{t.rich("startNewCalculationHint")}</span>
            <Button color="green" onClick={startNewCalculation}>
              {t("startNewCalculationAction")}
              <FontAwesomeIcon icon={faRotateRight} />
            </Button>
          </div>
        </div>
      </Card>
      <Card
        fullWidth
        className={clsx(styles.baseCard, styles.tipCard)}
        header={t("tipOfTheDay")}
      >
        <div className={styles.tipC}>
          <button
            className={styles.tipButton}
            disabled={isTipLoading}
            onClick={nextTip}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className={styles.tip}>{tip?.text}</div>
          <button
            className={styles.tipButton}
            disabled={isTipLoading}
            onClick={nextTip}
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
      </Card>
    </div>
  );
}

export default CalculatorSummary;
